/**
 * Sidebar — Verbund-Shell für rmnc-energie (portiert aus rmnc-personal,
 * siehe docs/SHELL_PORTIERUNG.md). Navigation: Dashboard, Zähler,
 * Energiearten & Preise, SEU, Produktionsdaten.
 *
 * Geschäftsführungs-Sonderfall (ROLE_GESCHAEFTSFUEHRUNG, ARCHITEKTUR.md §5):
 * sieht NUR den SEU-Eintrag. Statt eines separaten Guards wird das hier
 * direkt über `requiredRoles` an jedem Item außer SEU abgebildet — jedes
 * operative Item verlangt mindestens eine "breitere" Rolle
 * (admin/leitung/pflege/lesen). Die SeuOnlyGuard-Route-Logik in App.tsx
 * bleibt zusätzlich als serverseitig unabhängige Absicherung bestehen.
 */
import {
  LayoutDashboard,
  Gauge,
  Coins,
  Leaf,
  Factory,
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import {
  ROLE_ADMIN,
  ROLE_LEITUNG,
  ROLE_PFLEGE,
  ROLE_LESEN,
  useAuthStore,
} from "@/store/auth"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

/** Rollen, die vollen Zugriff auf die operativen Module haben (nicht nur SEU). */
const BREITE_ROLLEN = [ROLE_ADMIN, ROLE_LEITUNG, ROLE_PFLEGE, ROLE_LESEN, "admin"]

export interface NavLeaf {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  requiredRoles?: string[]
}

export const navItems: NavLeaf[] = [
  { title: "Dashboard", href: "/dashboard", icon: LayoutDashboard, requiredRoles: BREITE_ROLLEN },
  { title: "Zähler", href: "/zaehler", icon: Gauge, requiredRoles: BREITE_ROLLEN },
  { title: "Energiearten & Preise", href: "/energiearten", icon: Coins, requiredRoles: BREITE_ROLLEN },
  { title: "SEU / ISO 50001", href: "/seu", icon: Leaf },
  { title: "Produktionsdaten", href: "/produktionsdaten", icon: Factory, requiredRoles: BREITE_ROLLEN },
]

export function filterNavByRoles(
  entries: NavLeaf[],
  roles: readonly string[] | undefined | null,
): NavLeaf[] {
  const allowed = (required?: string[]) =>
    !required || required.length === 0 || (roles ?? []).some((r) => required.includes(r))
  return entries.filter((entry) => allowed(entry.requiredRoles))
}

interface SidebarProps {
  collapsed?: boolean
  /** App-Akzentfarbe als HSL-Triplet, überschreibt --primary lokal. Energie: Gelb. */
  accent?: string
}

export function Sidebar({ collapsed = false, accent }: SidebarProps) {
  const location = useLocation()
  const roles = useAuthStore((s) => s.user?.roles)

  const visibleItems = filterNavByRoles(navItems, roles)

  function isActive(href: string): boolean {
    return location.pathname === href || location.pathname.startsWith(href + "/")
  }

  function leafClasses(active: boolean): string {
    return cn(
      "flex items-center gap-3 border-l-4 border-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
      active && "sidebar-item-active",
      collapsed && "justify-center px-0",
    )
  }

  function renderLeaf(item: NavLeaf) {
    const Icon = item.icon
    const link = (
      <Link key={item.href} to={item.href} className={leafClasses(isActive(item.href))}>
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.title}</span>}
      </Link>
    )
    if (!collapsed) return link
    return (
      <Tooltip key={item.href}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.title}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <aside
      style={accent ? ({ "--primary": accent } as React.CSSProperties) : undefined}
      className={cn(
        "flex h-full flex-col border-r bg-[hsl(var(--sidebar-background))] text-[hsl(var(--sidebar-foreground))] transition-all duration-200",
        collapsed ? "w-16" : "w-60",
      )}
    >
      {/* FabrikOS-Plattformmarke: ausgeklappt Wortmarke, eingeklappt Symbol.
          Basis-bewusst: Dateien aus public/ werden von Vite NICHT umgeschrieben —
          nur Importe. Hart "/branding/…" landete im Pfad-Betrieb beim Portal,
          das seine index.html zurueckgab: 200 mit text/html statt eines Bildes,
          also ein kaputtes Logo ohne jeden Fehlereintrag. BASE_URL endet auf "/",
          deshalb hier ohne fuehrenden Schraegstrich. */}
      <div className={cn("flex h-14 items-center border-b px-4", collapsed && "justify-center px-0")}>
        <Link to="/" aria-label="FabrikOS">
          {collapsed ? (
            <img src={`${import.meta.env.BASE_URL}branding/fabrikos/symbol.jpg`} alt="FabrikOS" className="h-8 w-8 shrink-0" />
          ) : (
            <img src={`${import.meta.env.BASE_URL}branding/fabrikos/logo.svg`} alt="FabrikOS" className="h-6 w-auto" />
          )}
        </Link>
      </div>

      <ScrollArea className="flex-1">
        <nav className="flex flex-col gap-0.5 py-2">
          {visibleItems.map((entry) => renderLeaf(entry))}
        </nav>
      </ScrollArea>
    </aside>
  )
}
